/**
 * src/features/onboarding/ui/OnboardingChecklist.tsx
 *
 * INLINE activation checklist for new authenticated masters — ONBOARD-01/02.
 *
 * CRITICAL: This is NOT a modal/dialog/drawer. It is an inline panel rendered
 * in the editor header area, below TrialBanner. The editor remains fully usable
 * while the checklist is visible (ONBOARD-02).
 *
 * Gate: planLoaded && userId && !onboardingDone
 *   - planLoaded prevents the 200ms flash before GET /api/user/plan settles (Pitfall 3)
 *   - userId ensures only authenticated users see the checklist
 *   - !onboardingDone hides it once the user is done
 *
 * Auto-close: when completedCount===3, setTimeout(markOnboardingDone, 600) fires
 * so the user gets a brief visual confirmation before the panel exits.
 *
 * Dismiss (×): immediate markOnboardingDone() — local persist + fire-and-forget POST.
 */

import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuthStore } from '@/stores/useAuthStore';
import { useOnboardingStore } from '@/stores/useOnboardingStore';
import { useReducedMotion } from '@/shared/lib/hooks/useReducedMotion';
import { useOnboarding } from '../hooks/useOnboarding';

/** Auto-close delay (ms) after all 3 steps complete — gives visual confirmation. */
const AUTO_CLOSE_DELAY_MS = 600;
const TOTAL_STEPS = 3;

/**
 * Inner panel — rendered only when the gate passes. All hooks are called unconditionally here.
 */
function OnboardingPanel(): React.ReactElement {
  const { t } = useTranslation();
  const reduced = useReducedMotion();
  const { hasCreatedRoom, hasAddedService, hasExported, completedCount, markOnboardingDone } =
    useOnboarding();

  const autoCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Auto-close when all 3 steps are done (~600ms delay for visual confirmation)
  useEffect(() => {
    if (completedCount === TOTAL_STEPS) {
      autoCloseTimerRef.current = setTimeout(() => {
        markOnboardingDone();
      }, AUTO_CLOSE_DELAY_MS);
    }
    return () => {
      if (autoCloseTimerRef.current !== null) {
        clearTimeout(autoCloseTimerRef.current);
        autoCloseTimerRef.current = null;
      }
    };
    // markOnboardingDone is stable (defined in hook without external deps)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completedCount]);

  const progressPct = Math.round((completedCount / TOTAL_STEPS) * 100);

  const steps = [
    { key: 'step1', done: hasCreatedRoom, label: t('onboarding.step1', 'Dodaj pierwsze pomieszczenie') },
    { key: 'step2', done: hasAddedService, label: t('onboarding.step2', 'Przypisz usługi do pomieszczenia') },
    { key: 'step3', done: hasExported, label: t('onboarding.step3', 'Eksportuj kosztorys') },
  ];

  const firstIncompleteIndex = steps.findIndex((s) => !s.done);

  return (
    <motion.div
      key="onboarding-checklist"
      initial={{ opacity: 0, y: reduced ? 0 : -8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: reduced ? 0 : -8 }}
      transition={{ duration: reduced ? 0 : 0.25, ease: 'easeOut' }}
      className="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl p-4 shadow-card my-2"
    >
      {/* Header row: title | progress bar + fraction | dismiss button */}
      <div className="flex items-center gap-3 mb-3">
        <span className="text-subhead font-bold text-slate-800 dark:text-slate-100 shrink-0">
          {t('onboarding.title', 'Pierwsze kroki')}
        </span>

        <div className="flex-1 flex items-center gap-2 min-w-0">
          {/* Progress bar */}
          <div
            className="flex-1 h-1 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden"
            role="progressbar"
            aria-valuenow={completedCount}
            aria-valuemin={0}
            aria-valuemax={TOTAL_STEPS}
            aria-label={t('onboarding.progress', '{{done}}/{{total}}', { done: completedCount, total: TOTAL_STEPS })}
          >
            <div
              className="h-full bg-blue-600 rounded-full transition-[width] duration-300 ease-out"
              style={{ width: `${progressPct}%` }}
            />
          </div>

          {/* Fraction */}
          <span className="text-caption text-slate-400 shrink-0 tabular-nums">
            {t('onboarding.progress', '{{done}}/{{total}}', { done: completedCount, total: TOTAL_STEPS })}
          </span>
        </div>

        {/* Dismiss button */}
        <button
          type="button"
          onClick={markOnboardingDone}
          aria-label={t('onboarding.dismiss_aria', 'Odrzuć przewodnik')}
          className="flex items-center gap-1 text-caption text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors shrink-0 min-h-[44px] px-1"
        >
          <X size={14} aria-hidden="true" />
          <span className="hidden sm:inline">{t('onboarding.dismiss', 'Pomiń')}</span>
        </button>
      </div>

      {/* Step list */}
      <ol className="flex flex-col gap-2">
        {steps.map((step, idx) => {
          const isCurrentStep = !step.done && idx === firstIncompleteIndex;
          return (
            <li
              key={step.key}
              className="flex items-center gap-2"
              aria-current={isCurrentStep ? 'step' : undefined}
            >
              {step.done ? (
                // Subtle pop when a step gets checked off — an onboarding "moment".
                // Instant under reduced motion (no scale, no delay).
                <motion.span
                  initial={{ scale: reduced ? 1 : 0.5, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: reduced ? 0 : 0.2, ease: 'easeOut' }}
                  className="shrink-0"
                >
                  <CheckCircle2
                    size={20}
                    className="text-green-600 dark:text-green-400"
                    aria-hidden="true"
                  />
                </motion.span>
              ) : (
                <span
                  className="w-5 h-5 rounded-full border border-slate-300 dark:border-slate-700 flex items-center justify-center shrink-0"
                  aria-hidden="true"
                />
              )}
              <span
                className={
                  step.done
                    ? 'text-body line-through text-slate-400 dark:text-slate-500'
                    : isCurrentStep
                      ? 'text-body font-bold text-slate-800 dark:text-slate-100'
                      : 'text-body text-slate-700 dark:text-slate-200'
                }
              >
                {step.label}
              </span>
            </li>
          );
        })}
      </ol>
    </motion.div>
  );
}

/**
 * OnboardingChecklist — gate wrapper + AnimatePresence for smooth mount/exit.
 * Gate: planLoaded && userId && !onboardingDone
 */
export function OnboardingChecklist(): React.ReactElement | null {
  const { planLoaded, userId } = useAuthStore();
  const { onboardingDone } = useOnboardingStore();

  const show = planLoaded && Boolean(userId) && !onboardingDone;

  return (
    <AnimatePresence>
      {show && <OnboardingPanel />}
    </AnimatePresence>
  );
}
